<script setup lang="ts">
import { AppBar, NavBar, NotificationsFlyout, ToastNotifications } from '@/components/shared'
import { ErrorDialogBox } from '@/components/shared/dialog-boxes'
import { useNavigator } from '@/hooks'
import routes from '@/router/routes'
import useAccountStore from '@/stores/modules/account'
import usConfigStore from '@/stores/modules/config'
import useGlobalStore from '@/stores/modules/global'
import {
  JOB_DETAILS_URL_NAME,
  JOB_EDIT_URL_NAME,
  LOGIN_URL,
  LOGIN_URL_NAME,
  RESET_PASSWORD_URL_NAME,
  WORKER_SIGNAL_R
} from '@/utils/constants'
import { computed, onBeforeMount, onErrorCaptured, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { VApp, VMain } from 'vuetify/components'
import type NotificationType from './enum/NotificationType'
import SignalRGroupType from './enum/SignalRGroupType'
import { MainToWorkerMessageType, WorkerToMainMessageType } from './enum/SignalRWorkerMessageType'
import useJobsStore from './stores/modules/jobs'
import useWorkersStore from './stores/modules/workers'
import useRealtimeStore from './stores/modules/realtime'
import { logInfo } from './utils/helpers/logger'
import SignalRWorker from './workers/SignalRWorker.ts?worker'

const route = useRoute()
const navigator = useNavigator()
const globalStore = useGlobalStore()
const configStore = usConfigStore()
const accountStore = useAccountStore()
const jobsStore = useJobsStore()
const realtimeStore = useRealtimeStore()
const subscribedToJobActivityUpdates = ref(false)

const userLoggedInAndNavigated = computed(
  () => globalStore.isUserLoggedIn && route.name !== LOGIN_URL_NAME
)
const isUserLoggedIn = !!localStorage.getItem('userId')
const navigateToLogin = () => navigator?.navigateTo(LOGIN_URL)
const workersStore = useWorkersStore()
const signalRWorker = computed(() => workersStore.workers?.[WORKER_SIGNAL_R])

const currentRouteName = computed(() => route.name)

const validateLoginAndURLValidityAndPerformSetup = async () => {
  if (isUserLoggedIn) {
    await accountStore.getAccountPermissions()
    await configStore.getUnitConfiguration()
    setupSignalRWorker()
  }

  const validRouteNames = routes
    .map((route) => {
      if (route.children) {
        return route.children.map((childRoute) => childRoute.name)
      }
      return route.name
    })
    .flat()
  if (!currentRouteName.value || !validRouteNames.includes(currentRouteName.value))
    navigateToLogin()
}

const redirectToLoginIfInvalidURLOrUserIsNotLoggedIn = async () => {
  const currentRoute = navigator?.getCurrentRoute()

  if (isUserLoggedIn) {
    await accountStore.getProfile(localStorage.getItem('userId') as string)
    validateLoginAndURLValidityAndPerformSetup()
  }
  if (!currentRoute?.name || currentRoute?.name === RESET_PASSWORD_URL_NAME) {
    return
  }
  if (!isUserLoggedIn && currentRoute?.name !== LOGIN_URL_NAME) {
    navigateToLogin()
  }
}

const handleSignalRConnectionStatus = (status: NotificationType, message: string) => {
  logInfo(message)
}

const setupSignalRWorker = () => {
  const worker = new SignalRWorker()
  worker.postMessage({
    name: MainToWorkerMessageType.Connect,
    accessToken: globalStore.accessToken
  })

  worker.onmessage = (event) => {
    const { name, ...rest } = event.data
    switch (name) {
      case WorkerToMainMessageType.Connected: {
        workersStore.saveWorker(WORKER_SIGNAL_R, worker)
        handleSignalRConnectionStatus(rest.status, rest.message)
        break
      }
      default:
        break
    }
  }
}

const subscribeToJobActivityUpdates = async () => {
  if (!signalRWorker.value) return
  realtimeStore.subscribeToJobActivityUpdates(signalRWorker.value, navigator)
}

const subscribeToJobActivityAndDetailsUpdates = async () => {
  if (!signalRWorker.value) return
  realtimeStore.subscribeToJobActivityAndDetailsUpdates(signalRWorker.value, navigator)
}

const unsubscribeToJobDetailsUpdates = () => {
  if (!signalRWorker.value) return
  realtimeStore.unsubscribeToJobDetailsUpdates(signalRWorker.value, navigator)
}

const unsubscribeToJobActivityUpdates = async () => {
  if (!signalRWorker.value) return
  realtimeStore.unsubscribeToJobActivityUpdates(signalRWorker.value, navigator)
}

onErrorCaptured((error) => {
  console.error('Error:', error)
})

onBeforeMount(() => {
  redirectToLoginIfInvalidURLOrUserIsNotLoggedIn()
})

watch(
  () => [currentRouteName.value, signalRWorker.value],
  async () => {
    if (!isUserLoggedIn) return

    if (
      currentRouteName.value !== JOB_EDIT_URL_NAME &&
      currentRouteName.value !== JOB_DETAILS_URL_NAME
    ) {
      await unsubscribeToJobDetailsUpdates()
      await subscribeToJobActivityUpdates()
    }

    if (
      currentRouteName.value == JOB_EDIT_URL_NAME ||
      currentRouteName.value == JOB_DETAILS_URL_NAME
    ) {
      await subscribeToJobActivityAndDetailsUpdates()
    }
  }
)
</script>

<template>
  <v-app class="position-relative z-0" :logged-in="globalStore.isUserLoggedIn">
    <NavBar v-if="userLoggedInAndNavigated" v-model="globalStore.isNavbarCollapsed" />
    <AppBar v-if="userLoggedInAndNavigated" :navbar-collapsed="globalStore.isNavbarCollapsed" />
    <v-main
      :navbar-collapsed="globalStore.isNavbarCollapsed"
      :is-nested-path="globalStore.hasBreadcrumbs"
      :class="`main-container d-flex flex-column ${globalStore.isUserLoggedIn ? 'main-container--padding' : ''}`"
    >
      <router-view :navbar-collapsed="globalStore.isNavbarCollapsed" #default="{ Component }">
        <component :is="Component" />
      </router-view>
    </v-main>
    <ErrorDialogBox />
    <NotificationsFlyout />
    <ToastNotifications />
  </v-app>
</template>

<style scoped lang="scss">
header {
  line-height: 1.5;
  max-height: 100vh;
}

.logo {
  display: block;
  margin: 0 auto 2rem;
}

nav {
  width: 100%;
  font-size: 12px;
  text-align: center;
  margin-top: 2rem;
}

nav a.router-link-exact-active {
  color: var(--color-text);
}

nav a.router-link-exact-active:hover {
  background-color: transparent;
}

nav a {
  display: inline-block;
  padding: 0 1rem;
  border-left: 1px solid var(--color-border);
}

nav a:first-of-type {
  border: 0;
}

@media (min-width: 1024px) {
  header {
    display: flex;
    place-items: center;
    padding-right: calc(var(--section-gap) / 2);
  }

  .logo {
    margin: 0 2rem 0 0;
  }

  header .wrapper {
    display: flex;
    place-items: flex-start;
    flex-wrap: wrap;
  }

  nav {
    text-align: left;
    margin-left: -1rem;
    font-size: 1rem;

    padding: 1rem 0;
    margin-top: 1rem;
  }
}
</style>
